import moment from "moment";
import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DATE_FORMATS } from "../../../constants";
import useScrollspyStickyHeader from "../../../hooks/useScrollspyStickyHeader";
import { RillContext } from "../../../rill/RillReact/share/base";
import { isFemaleCycleSectionEnabledFromConfig } from "../../../utils/client-configuration-helpers";
import CalendarCategoryFilterBar from "./components/CalendarCategoryFilterBar";
import CalendarCollapseToggle from "./components/CalendarCollapseToggle";
import {
  categoryKeysWithPeriod,
  PLAN_CATEGORY_KEYS,
  planCategoryLabel,
} from "./components/calendarFilterCategories";
import CalendarMonthHeader from "./components/CalendarMonthHeader";
import PlanWeekRow, { PlanDayHeaderLabel } from "./components/PlanWeekRow";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

const weeksInMonth = [...Array(6)].map((_, i) => i);
const daysInWeek = [...Array(7)].map((_, i) => i);

const PlanView = () => {
  const { locals } = useContext(RillContext);
  const periodEnabled = isFemaleCycleSectionEnabledFromConfig(
    locals.clientConfiguration
  );
  const categoryKeys = useMemo(
    () => categoryKeysWithPeriod(PLAN_CATEGORY_KEYS, periodEnabled),
    [periodEnabled]
  );
  const [activeFilters, setActiveFilters] = useState(() => [
    ...categoryKeysWithPeriod(PLAN_CATEGORY_KEYS, periodEnabled),
  ]);
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);

  const handleFilterToggle = useCallback((key) => {
    setActiveFilters((current) => {
      if (current.includes(key)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== key);
      }
      return [...current, key];
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setActiveFilters([...categoryKeys]);
  }, [categoryKeys]);

  const handleClear = useCallback(() => {
    setActiveFilters(["annualPhases"]);
  }, []);

  const {
    activeMonth,
    query,
    startOfFirstWeek,
    athlete,
    pathname,
    planExercises = {},
    planNutritionPlans = [],
    planMealPlans = [],
    planHabits = [],
    planAnnualPhases = [],
    planAnnualEvents = [],
    planTargetGoals = [],
    planFemaleCycleDays = [],
    planTrackingItems = {},
    planCheckInDays = [],
    cycles = [],
  } = locals;

  const units = locals.activeUser?.units || "metric";

  // Pulled out of the render loop (mirrors LogView.js's own visibleWeeks) so
  // rowCount — how many weeks actually render this month, always 4-6, never
  // a fixed 6 — is known before wiring the scrollspy hook, and so the
  // contiguous 0..rowCount-1 row index (not the raw 0-5 weekIndex, which can
  // skip 5) lines up with what registerRow expects.
  const visibleWeeks = useMemo(() => {
    return weeksInMonth
      .map((weekIndex) => {
        const weekStart = startOfFirstWeek.clone().add(weekIndex, "weeks");
        if (weekIndex === 5 && weekStart.month() !== activeMonth.month()) {
          return null;
        }
        const weekDays = daysInWeek.map((dayIndex) => {
          const day = weekStart.clone().add(dayIndex, "days");
          const dayKey = day.format(DATE_DAY_FORMAT);
          return {
            day,
            dayKey,
            isCurrentMonth: day.month() === activeMonth.month(),
            isToday:
              day.format("YYYY-MM-DD") === moment.utc().format("YYYY-MM-DD"),
            exercises: planExercises[dayKey] || [],
          };
        });
        return { weekIndex, weekDays };
      })
      .filter(Boolean);
  }, [startOfFirstWeek, activeMonth, planExercises]);

  // Sticky day-header (scrollspy) — mirrors LogView.js's own wiring, see
  // useScrollspyStickyHeader.js for the full "why" (root: .calendar-month-
  // wrapper's own bounded internal scroll, not the page).
  const calendarMonthWrapperRef = useRef(null);
  const {
    registerRow,
    stickyHeadRef,
    activeIndex,
    colWidths,
    colCardWidths,
    containerRect,
    onScrollSync,
  } = useScrollspyStickyHeader({
    rootRef: calendarMonthWrapperRef,
    rowCount: visibleWeeks.length,
    getHeaderCells: (row) =>
      Array.from(row.querySelectorAll(".plan-day-column")),
    deps: [collapsed, visibleWeeks],
  });
  const activeWeekDays =
    activeIndex != null ? visibleWeeks[activeIndex]?.weekDays : null;

  // Same reset as LogView.js — navigating to a different month keeps this
  // component mounted, so without this the wrapper's old scrollTop carries
  // over and the scrollspy briefly shows a stale header for the new month.
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeMonth is a re-run trigger, not a value read in the closure
  useLayoutEffect(() => {
    if (calendarMonthWrapperRef.current) {
      calendarMonthWrapperRef.current.scrollTop = 0;
    }
  }, [activeMonth]);

  return (
    <main className="Calendar PlanView">
      <CalendarMonthHeader
        activeMonth={activeMonth}
        athlete={athlete}
        pathname={pathname}
        query={query}
        locals={locals}
        viewOptions={[]}
      />
      <div className="flex justify-between items-center">
        <CalendarCategoryFilterBar
          activeFilters={activeFilters}
          onToggle={handleFilterToggle}
          onToggleAll={handleToggleAll}
          onClear={handleClear}
          categoryKeys={PLAN_CATEGORY_KEYS}
          labelForKey={planCategoryLabel}
          periodEnabled={periodEnabled}
        />

        <CalendarCollapseToggle
          collapsed={collapsed}
          onToggle={toggleCollapsed}
        />
      </div>

      {activeWeekDays && (
        <div
          className="plan-sticky-head-fixed"
          style={{
            top: containerRect.top,
            left: containerRect.left,
            width: containerRect.width,
          }}
        >
          <div
            ref={stickyHeadRef}
            className="plan-sticky-head-inner"
            style={{
              gridTemplateColumns: colWidths.map((w) => `${w}px`).join(" "),
            }}
          >
            {activeWeekDays.map((d, i) => (
              <div
                key={d.dayKey}
                className={`plan-sticky-head-cell${
                  !d.isCurrentMonth ? " other-month" : ""
                }`}
                style={{ width: colCardWidths[i] }}
              >
                <div className="day-header">
                  <PlanDayHeaderLabel day={d.day} isToday={d.isToday} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="calendar-month-wrapper" ref={calendarMonthWrapperRef}>
        <div
          className="plan-view-weeks"
          style={{ overflowX: "auto" }}
          onScroll={onScrollSync}
        >
          {visibleWeeks.map(({ weekIndex, weekDays }, rowIndex) => (
            <PlanWeekRow
              key={weekIndex}
              ref={registerRow(rowIndex)}
              collapsed={collapsed}
              weekDays={weekDays}
              activeFilters={activeFilters}
              nutritionPlans={planNutritionPlans}
              mealPlans={planMealPlans}
              habits={planHabits}
              annualPhases={planAnnualPhases}
              annualEvents={planAnnualEvents}
              targetGoals={planTargetGoals}
              units={units}
              femaleCycleDays={planFemaleCycleDays}
              trackingItems={planTrackingItems}
              checkInDays={planCheckInDays}
              cycles={cycles}
            />
          ))}
        </div>
      </div>
    </main>
  );
};

export default PlanView;
